import {
	type CaptureFileJSONObject,
	type CollectionFieldVectorFieldValue,
	type CollectionModelFieldAnchorValue,
	IkeFieldType,
	IkeUnitType,
	type LocationData,
	MarkerType,
} from '@ikegps/core-api';
import type { CollectionModelFieldDate } from './date.field';
import type { CollectionModelFieldFileTool } from './file.tool.field';
import type { CollectionModelFieldLocation } from './location.field';
import { MultiPoint } from '../../../math/multipoint';
import { IkeUnit } from '../../../unit';
import type { FormModelField } from '../../form/form.field';
import { CollectionModel } from '../collection.data';
import { CollectionModelField, ModelMutator } from '../collection.field';
import { CollectionModelDrawableMarker } from '../drawable/marker.drawable';
import { registerFieldType } from './field.registry';
import type { CaptureProvider } from './provider/capture.provider';
import type { MeasurementProvider, MeasurementProviderValue } from './provider/measurement.provider';

export interface ModelFieldStateHeight {
	provider: 'manual' | 'spanheight' | 'photomeasure' | 'photomeasuremanual';
	value?: number;
	captures?: string[];

	drawable?: string;
	captureId?: string;
}

export class ModelMutatorHeight extends ModelMutator<FormModelField, CollectionModelFieldHeight> {
	/**
	 * Remove the state of this drawable
	 * *Warning:* You will need to unlink & remove the drawable from the list before destroying the state.
	 * @see CollectionModelDrawableStateMutator.remove
	 */
	destroyState() {
		this.state.setFieldValue(this.field.id, null);
	}

	override clear() {
		this.manual(null);
	}

	manualFromInput(newValue: number) {
		const { stateValue } = this.field;

		if (!newValue || isNaN(newValue) || !stateValue) {
			this.unlink();
			this.state.setFieldValue<ModelFieldStateHeight>(this.field.id, null);
		} else {
			const drawableState = this.field.collection.getDrawableState(stateValue.captureId);
			const drawable = drawableState.getDrawable<CollectionModelDrawableMarker>(stateValue.drawable);

			const { x, y } = drawable.calculateM2Px(newValue);

			drawable.moveNoBounds(x, y);
		}
	}

	manual(newValue: number | null) {
		if (this.field.isPhotoMeasure()) {
			this.unlink();
		}

		if (newValue == null || isNaN(newValue)) {
			this.state.setFieldValue<ModelFieldStateHeight>(this.field.id, null);
		} else {
			this.state.setFieldValue<ModelFieldStateHeight>(this.field.id, {
				provider: CollectionModelFieldHeight.PROVIDER_MANUAL,
				value: newValue,
			});
		}
	}

	spanHeight(captureIds: string[]) {
		if (this.field.isPhotoMeasure()) {
			this.unlink();
		}

		if (!Array.isArray(captureIds)) {
			throw new Error(`Field ${this.field.id}: Invalid capture state.`);
		}
		if (captureIds.length !== 2) {
			throw new Error(`Field ${this.field.id}: Invalid capture count "${captureIds.length}".`);
		}

		this.state.setFieldValue(this.field.id, {
			provider: CollectionModelFieldHeight.PROVIDER_SPAN_HEIGHT,
			captures: captureIds.slice(),
		});
	}

	drawable(drawable: CollectionModelDrawableMarker, isManual: boolean = false) {
		if (this.field.isPhotoMeasure() || (this.field.isPhotoMeasureManual() && !isManual)) {
			this.unlink();
		}

		this.state.setFieldValue(this.field.id, {
			provider: isManual
				? CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE_MANUAL
				: CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE,
			drawable: drawable.id,
			captureId: drawable.captureId,
		});

		if (!drawable.label.links.some((link) => link.id === this.field.id)) {
			drawable.label.linkHeight(this.field);
		}
	}

	unlink() {
		const stateValue = this.field.stateValue;

		// Only unlink if currently linked.
		if (!stateValue?.drawable) {
			return;
		}

		this.destroyState();

		if (stateValue == null || stateValue.drawable == null || stateValue.captureId == null) {
			return;
		}

		const drawableState = this.field.collection.getDrawableState(stateValue.captureId);

		if (drawableState.hasDrawable(stateValue.drawable)) {
			const drawable = drawableState.getDrawable<CollectionModelDrawableMarker>(stateValue.drawable);
			drawable.label.unlink(this.field.id);
		}
	}

	override copy(from: CollectionModelFieldHeight): boolean {
		if (from.isManual()) {
			this.manual(from.value);
			return true;
		}

		if (from.isSpanHeight()) {
			const state = from.getCaptures().map((capture) => capture.objectId);
			this.spanHeight(state);
			return true;
		}

		if (from.isPhotoMeasure() || from.isPhotoMeasureManual()) {
			const fromCollection = from.collection;
			const toCollection = this.field.collection;

			const captureId = from.stateValue?.captureId;
			if (!captureId || !from.stateValue?.drawable) {
				return false;
			}

			const drawable = fromCollection
				.getDrawableState(captureId)
				.getDrawable<CollectionModelDrawableMarker>(from.stateValue.drawable);

			if (fromCollection === toCollection) {
				// Link field to existing marker
				drawable.label.linkHeight(this.field);
				this.drawable(drawable);
				return true;
			}

			// Create a new marker if it doesn't already exist
			const toDrawableState = toCollection.getDrawableState(captureId);

			if (!toDrawableState.drawables.has(drawable.id)) {
				const newDrawable = drawable.clone(toDrawableState);

				this.drawable(newDrawable);

				if (toDrawableState.base == null) {
					this.copyBaseMarkers(fromCollection, captureId);
				}
			}

			return true;
		}

		return false;
	}

	// duplicated in vertical.separation.field.ts
	private copyBaseMarkers(fromCollection: CollectionModel, captureId: string) {
		const collection = this.field.collection;

		const drawableState = collection.getDrawableState(captureId);
		const fromDrawableState = fromCollection.getDrawableState(captureId);

		const fromBaseMarker = fromDrawableState.base;
		if (fromBaseMarker != null) {
			const { marker: baseMarker } = CollectionModelDrawableMarker.createWithLabel(
				drawableState,
				fromBaseMarker.getVector(),
				fromBaseMarker.label.getVector(),
				undefined,
				MarkerType.BASE
			);

			const fromBaseOffsetMarker = fromDrawableState.baseOffset;
			if (fromBaseOffsetMarker != null) {
				baseMarker.offset = fromBaseMarker.offset;
			}
		}
	}
}

export class CollectionModelFieldHeight
	extends CollectionModelField<FormModelField, ModelFieldStateHeight>
	implements CaptureProvider, MeasurementProvider
{
	static readonly PROVIDER_MANUAL = 'manual';
	static readonly PROVIDER_SPAN_HEIGHT = 'spanheight';
	static readonly PROVIDER_PHOTO_MEASURE = 'photomeasure';
	static readonly PROVIDER_PHOTO_MEASURE_MANUAL = 'photomeasuremanual';

	static TYPE: IkeFieldType = IkeFieldType.HEIGHT;

	static isHeightField(f: CollectionModelField): f is CollectionModelFieldHeight {
		return f.schema.type === CollectionModelFieldHeight.TYPE;
	}

	private _Mutator: ModelMutatorHeight;
	get Mutator() {
		if (this._Mutator == null) {
			this._Mutator = new ModelMutatorHeight(this);
		}
		return this._Mutator;
	}

	/* computed */
	override get value(): number {
		const stateValue = this.stateValue;
		if (stateValue == null) {
			return null;
		}
		if (stateValue.provider === CollectionModelFieldHeight.PROVIDER_MANUAL) {
			return stateValue.value;
		}

		if (stateValue.provider === CollectionModelFieldHeight.PROVIDER_SPAN_HEIGHT) {
			const p2pMeasurement = MultiPoint.Points(this.getCaptures());
			return Math.abs(p2pMeasurement.vertical);
		}

		if (stateValue.provider === CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE) {
			return this.drawable.value;
		}

		if (stateValue.provider === CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE_MANUAL) {
			return this.drawable.value;
		}

		throw new Error(`Field ${this.id}: Invalid Provider type "${stateValue.provider}"`);
	}

	override get annotationLabel(): string {
		//if we're self-title no need to search for field just return displayname
		const fieldId = this.schema.annotationLabelFieldId;
		if (fieldId === this.schema.id) {
			return this.schema.displayName;
		}

		const field = this.findAncestorField(this.parent, fieldId);
		if (!field || !field.schema.type) {
			return this.parent.title;
		}

		switch (field.schema.type) {
			case IkeFieldType.ANCHOR:
				// I think to get the complex values we have to type them first and put them together ourselves
				if (field.hasValue()) {
					let anchor: CollectionModelFieldAnchorValue = field.value as CollectionModelFieldAnchorValue;
					return `${anchor.collectionTool} ${anchor.placement}`;
				}
				return field.schema.displayName;
			case IkeFieldType.DATE:
				let date: CollectionModelFieldDate = field as CollectionModelFieldDate;
				if (!date.hasValue() || date.displayValue == null || date.displayValue === '') {
					return this.parent.title;
				}
				return date.displayValue;
			case IkeFieldType.FILE_TOOL:
				let fileTool: CollectionModelFieldFileTool = field as CollectionModelFieldFileTool;
				if (!fileTool.hasValue() || fileTool.displayValue == null || fileTool.displayValue === '') {
					return this.parent.title;
				}
				return fileTool.displayValue;
			case IkeFieldType.FORM:
				return field.schema.displayName;
			case IkeFieldType.HEIGHT:
				return field.schema.displayName;
			case IkeFieldType.IMAGE:
				return field.schema.displayName;
			case IkeFieldType.LOCATION:
				let loc: CollectionModelFieldLocation = field as CollectionModelFieldLocation;
				if (!loc.hasValue() || loc.displayValue == null || loc.displayValue === '') {
					return this.parent.title;
				}
				let locVal = loc.value as LocationData;
				return `${locVal.latitude.toFixed(2)},${locVal.longitude.toFixed(2)}`;
			case IkeFieldType.NESTED_LIST:
				if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
					return this.parent.title;
				}
				return field.displayValue;
			case IkeFieldType.NUMBER:
				if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
					return this.parent.title;
				}
				return field.displayValue;
			case IkeFieldType.SELECT_LIST:
				if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
					return this.parent.title;
				}
				return field.displayValue;
			case IkeFieldType.TEXT:
				if (!field.hasValue() || field.displayValue == null || field.displayValue === '') {
					return this.parent.title;
				}
				return field.displayValue;
			case IkeFieldType.TRUE_SIZE:
				return field.schema.displayName;
			case IkeFieldType.VECTOR:
				let vector: CollectionFieldVectorFieldValue = field.value;
				if (field.hasValue()) {
					return `${IkeUnit.display(vector.distance, this.distanceUnit, IkeUnit.METER, IkeUnitType.DISTANCE, 1)} ${IkeUnit.display(vector.bearing, IkeUnit.DEGREE, IkeUnit.RADIAN, IkeUnitType.ANGLE, 1)}`;
				}
				return field.schema.displayName;
			case IkeFieldType.VERTICAL_SEPARATION:
				return field.schema.displayName;
			case IkeFieldType.YES_NO:
				return field.schema.displayName;
			default:
				return this.parent.title;
		}
	}

	get drawable(): CollectionModelDrawableMarker {
		const drawableState = this.collection.getDrawableState(this.stateValue.captureId);
		return drawableState.getDrawable<CollectionModelDrawableMarker>(this.stateValue.drawable);
	}

	hasValue(): boolean {
		const stateValue = this.stateValue;
		if (stateValue == null) {
			return false;
		}
		return true;
	}

	get provider(): 'manual' | 'spanheight' | 'photomeasure' | 'photomeasuremanual' {
		const stateValue = this.stateValue;
		if (stateValue == null || stateValue.provider == null) {
			return CollectionModelFieldHeight.PROVIDER_MANUAL;
		}
		return stateValue.provider;
	}

	isManual() {
		return this.provider === CollectionModelFieldHeight.PROVIDER_MANUAL;
	}

	isPhotoMeasure() {
		return this.provider === CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE;
	}

	isPhotoMeasureManual() {
		return this.provider === CollectionModelFieldHeight.PROVIDER_PHOTO_MEASURE_MANUAL;
	}

	isSpanHeight() {
		return this.provider === CollectionModelFieldHeight.PROVIDER_SPAN_HEIGHT;
	}

	getMeasurements(): MeasurementProviderValue[] {
		if (!this.isPhotoMeasure()) {
			return [];
		}
		const stateValue = this.stateValue;
		if (stateValue == null) {
			return [];
		}
		return [
			{
				captureId: stateValue.captureId,
				drawableId: stateValue.drawable,
			},
		];
	}

	getOwnedCaptures(): CaptureFileJSONObject[] {
		if (!this.isSpanHeight()) {
			return [];
		}

		const stateValue = this.stateValue;
		if (stateValue == null) {
			return [];
		}

		return stateValue.captures?.map((c) => this.collection.state.getCapture(c)).filter((c) => !!c) ?? [];
	}

	getCaptures(): CaptureFileJSONObject[] {
		return this.getCaptureIds()
			.map((c) => this.collection.state.getCapture(c))
			.filter((c) => !!c);
	}

	getOwnedCaptureIds(): string[] {
		if (!this.isSpanHeight()) {
			return [];
		}

		const stateValue = this.stateValue;
		if (stateValue == null) {
			return [];
		}

		return stateValue.captures ?? [];
	}

	getCaptureIds(): string[] {
		if (this.isPhotoMeasure()) {
			return this.stateValue?.captureId ? [this.stateValue?.captureId] : [];
		}

		if (!this.isSpanHeight()) {
			return [];
		}

		const stateValue = this.stateValue;
		if (stateValue == null) {
			return [];
		}

		return stateValue.captures ?? [];
	}
}

registerFieldType(IkeFieldType.HEIGHT, (parent, schema) => new CollectionModelFieldHeight(parent, schema));
